import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { errorSchemaIsValid } from 'platform/forms-system/src/js/validation';
import { mask, capitalizeFirstLetter } from '../../utils/helpers';
import { hasEnteredBankAccount } from './DirectDepositField';

export default function DirectDepositViewField({
  formData,
  formContext,
  errorSchema,
  startEditing,
}) {
  const prefilledBankAccount = useSelector(
    state => state.form?.data?.['view:prefilledBankAccount'],
  );
  const { accountType, bankName, accountNumber, routingNumber } =
    formData || {};
  const hasUserBankData = hasEnteredBankAccount(formData);
  const schemaHasErrors = !errorSchemaIsValid(errorSchema);

  // A prefilled account is valid on-file data, so it's shown (and never flagged
  // as missing) unless the veteran has entered a complete replacement account
  const showPrefilled =
    !!prefilledBankAccount && (!hasUserBankData || schemaHasErrors);
  const hasErrors = schemaHasErrors && !prefilledBankAccount;

  const accountTypeDisplay = accountType
    ? `${capitalizeFirstLetter(accountType)} account`
    : 'Account';

  const editButtonClasses = ['edit-button', 'vads-u-margin-top--1'].join(' ');

  useEffect(() => {
    if (hasErrors) {
      formContext.onError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors]);

  return (
    <>
      <div className="vads-u-margin-left--0">
        <h3 tabIndex={-1} className="review-card--title vads-u-margin-top--0">
          {showPrefilled
            ? `${capitalizeFirstLetter(
                prefilledBankAccount['view:accountType'],
              )} account`
            : accountTypeDisplay}
        </h3>
        <p className="vads-u-margin-bottom--2">
          Bank routing number:{' '}
          {showPrefilled
            ? prefilledBankAccount['view:routingNumber']?.replaceAll('*', '●')
            : mask(routingNumber)}
        </p>
        <p className="vads-u-margin-y--2">
          Account number:{' '}
          {showPrefilled
            ? prefilledBankAccount['view:accountNumber']?.replaceAll('*', '●')
            : mask(accountNumber)}
        </p>
        <p className="vads-u-margin-bottom--0">
          Bank name:{' '}
          {showPrefilled ? prefilledBankAccount['view:bankName'] : bankName}
        </p>
      </div>
      {hasErrors && (
        <va-alert
          class="vads-u-margin-top--4"
          data-testid="direct-deposit-error-alert"
          slim
          status="error"
        >
          Banking information is missing or invalid. Please make sure it’s
          correct.
        </va-alert>
      )}
      <div className="vads-u-text-align--center">
        <va-button
          class={editButtonClasses}
          data-testid="direct-deposit-edit-button"
          full-width
          onClick={() => {
            startEditing();
            if (hasErrors) {
              formContext.onError();
            }
          }}
          uswds
          text="Edit your account details"
        />
      </div>
    </>
  );
}

DirectDepositViewField.propTypes = {
  errorSchema: PropTypes.object.isRequired,
  formContext: PropTypes.shape({
    onError: PropTypes.func.isRequired,
  }).isRequired,
  startEditing: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    accountType: PropTypes.string,
    accountNumber: PropTypes.string,
    routingNumber: PropTypes.string,
    bankName: PropTypes.string,
  }),
};
